import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addProduct,
  getActiveStores,
  getCalculatedData,
  getFrete,
} from "../../../components/products/functions";
import { getUsers, getUserAddresses } from "../../../components/users/functions";
import RegisterModal from "../../../components/users/registerModal";
import { statusObject } from "../../../components/utils/allProductStatus";
import {
  gradingTypes,
  gradingTypeSupportsAutograph,
  getGradingTypeLabel,
  getPreLacreFormatLabel,
  normalizeGradingType,
  preLacreFormatLabels,
  priorityTypes,
} from "../../../components/utils/gradingTypes";
import {
  cashnator,
  cashnatorFloat,
  cpfMask,
  decashnator,
  generateRandomID,
  isNumeric,
  phoneMask,
  formatarDataCompleta,
} from "../../../components/utils/functions";
import ToastContext from "../../../context/toastProvider";
import MobilePage from "../../ui/MobilePage";
import MobileSection from "../../ui/MobileSection";
import MobileStickyActions from "../../ui/MobileStickyActions";
import MobileFormField from "../../ui/MobileFormField";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileNewAddressSheet from "./MobileNewAddressSheet";
import { useCreateProductWizard } from "./useCreateProductWizard";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha";
const selectClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha";
const primaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";
const secondaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao disabled:opacity-50";

const STEP_TITLES = {
  user: "Usuário",
  delivery: "Entrega",
  product: "Dados do pedido",
  shipping: "Frete",
  checkout: "Dados calculados",
  status: "Status",
  comments: "Comentários",
  summary: "Resumo do pedido",
};

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 border-b border-primary/20 py-2 text-sm last:border-0">
      <span className="text-laranjinha/60">{label}</span>
      <span className="text-right text-laranjinha">{value}</span>
    </div>
  );
}

function SelectableCard({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border p-4 text-left ${
        selected ? "border-primary bg-primary/15" : "border-primary/30 bg-mgs-bg"
      }`}
    >
      {children}
    </button>
  );
}

export default function MobileCreateProduct({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { goodToast, badToast } = useContext(ToastContext);

  const prefillUser =
    location?.state?.origin === "users-new" ? location?.state?.user : null;

  const wizard = useCreateProductWizard({
    initialUserData: prefillUser || undefined,
    initialStep: prefillUser?._id ? "delivery" : "user",
  });

  const [stores, setStores] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // user step local state
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchCpf, setSearchCpf] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);

  // delivery step
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [createAddressOpen, setCreateAddressOpen] = useState(false);

  // shipping step
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState(false);

  // checkout step
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [insurance, setInsurance] = useState("R$0,00");
  const [finalPrice, setFinalPrice] = useState("R$0,00");
  const [deadline, setDeadline] = useState("");
  const [tipoFrete, setTipoFrete] = useState("");
  const [valorFrete, setValorFrete] = useState("R$0,00");
  const [enableCheckoutEdit, setEnableCheckoutEdit] = useState(false);

  useEffect(() => {
    getActiveStores().then((data) => {
      if (data?.type === "success") setStores(data.data ?? []);
      else badToast(data?.message);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAddresses = useCallback(async () => {
    if (!wizard.userData?._id) return;
    setAddressesLoading(true);
    const data = await getUserAddresses(user?.token, wizard.userData._id);
    if (data?.type === "success") setAddresses(data.data ?? []);
    setAddressesLoading(false);
  }, [user?.token, wizard.userData?._id]);

  useEffect(() => {
    if (wizard.step === "delivery" && wizard.deliveryData?.delivery === "correios") {
      loadAddresses();
    }
  }, [wizard.step, wizard.deliveryData?.delivery, loadAddresses]);

  useEffect(() => {
    if (wizard.step !== "shipping") return;
    let active = true;
    (async () => {
      setShippingLoading(true);
      setShippingError(false);
      const { productData, deliveryData } = wizard;
      const data = await getFrete(
        deliveryData?.address?.postalCode,
        productData?.gradingType,
        productData?.numberOfCards,
        productData?.priority,
        (productData?.declaredValue * 3) / 100,
        productData?.declaredValue
      );
      if (!active) return;
      if (data?.type !== "success") {
        setShippingError(true);
        badToast("Erro ao obter dados de frete!");
        setShippingLoading(false);
        return;
      }
      setShippingOptions(data.data ?? []);
      setShippingLoading(false);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard.step]);

  useEffect(() => {
    if (wizard.step !== "checkout") return;
    let active = true;
    (async () => {
      setCheckoutLoading(true);
      const { productData, deliveryData } = wizard;
      const insuranceVal =
        productData?.insurance ??
        Math.round((productData?.declaredValue * 3) / 100);
      const data = await getCalculatedData(
        user?.token,
        productData?.gradingType,
        productData?.numberOfCards,
        productData?.priority,
        insuranceVal,
        deliveryData?.store,
        deliveryData?.delivery,
        deliveryData?.shipping?.price,
        productData?.autographEvaluation
      );
      if (!active) return;
      if (data?.type !== "success") {
        badToast("Erro no cálculo do preço e prazo");
        setCheckoutLoading(false);
        return;
      }
      if (!productData?.finalPrice) {
        setFinalPrice(cashnator(data.data?.finalPrice));
      }
      if (!productData?.deadline) {
        setDeadline(String(data.data?.deadline ?? ""));
      }
      setInsurance(cashnator(insuranceVal));
      setTipoFrete(
        deliveryData?.shipping?.service || productData?.shippingService || ""
      );
      setValorFrete(
        cashnatorFloat(deliveryData?.shipping?.price || productData?.shipping || 0)
      );
      setCheckoutLoading(false);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard.step]);

  const handleSearchUsers = async () => {
    setSearchLoading(true);
    setSearchResults([]);
    const data = await getUsers(
      user?.token,
      searchName,
      searchEmail,
      searchCpf,
      searchPhone
    );
    if (data?.type !== "success") {
      badToast(data?.message);
      setSearchLoading(false);
      return;
    }
    setSearchResults(data.data ?? []);
    setSearchLoading(false);
  };

  const handleAddressCreated = () => {
    loadAddresses();
  };

  const handleUserCreated = (newUser) => {
    wizard.setUserData(newUser);
    setCreateUserOpen(false);
    wizard.next();
  };

  const handleNumberOfCards = (value) => {
    if (!isNumeric(value)) return;
    let n = parseInt(value, 10);
    let cards = value;
    if (n > 1200) {
      n = 1200;
      cards = "1200";
    }
    let priority = wizard.productData?.priority || "low";
    if (n > 84 && priority === "none") priority = "low";
    if (n > 126 && ["none", "low"].includes(priority)) priority = "mid";
    if (n > 252 && ["none", "low", "mid"].includes(priority)) priority = "high";
    if (n > 504 && ["none", "low", "mid", "high"].includes(priority)) priority = "max";
    wizard.setProductData((p) => ({ ...p, numberOfCards: cards, priority }));
  };

  const handleDeclaredValue = (value) => {
    if (!value) {
      wizard.setProductData((p) => ({ ...p, declaredValue: 0 }));
      return;
    }
    const num = decashnator(value);
    if (isNumeric(num)) {
      wizard.setProductData((p) => ({ ...p, declaredValue: num }));
    }
  };

  const handleRecalculate = async () => {
    setCheckoutLoading(true);
    const { productData, deliveryData } = wizard;
    const data = await getCalculatedData(
      user?.token,
      productData?.gradingType,
      productData?.numberOfCards,
      productData?.priority,
      decashnator(insurance),
      deliveryData?.store,
      deliveryData?.delivery,
      parseInt(decashnator(valorFrete), 10) / 100,
      productData?.autographEvaluation
    );
    if (data?.type !== "success") {
      badToast("Erro no cálculo do preço e prazo");
      setCheckoutLoading(false);
      return;
    }
    setFinalPrice(cashnator(data.data?.finalPrice));
    setDeadline(String(data.data?.deadline ?? ""));
    setCheckoutLoading(false);
  };

  const handleNext = () => {
    if (wizard.step === "shipping" && wizard.selectedShipping) {
      wizard.setDeliveryData((d) => ({
        ...d,
        shipping: wizard.selectedShipping,
      }));
      wizard.setProductData((p) => ({
        ...p,
        shippingService: wizard.selectedShipping?.service,
        shipping: wizard.selectedShipping?.price,
      }));
    }
    if (wizard.step === "checkout") {
      wizard.setProductData((p) => ({
        ...p,
        finalPrice: decashnator(finalPrice),
        deadline,
        insurance: decashnator(insurance),
        shippingService: tipoFrete,
        shipping: parseInt(decashnator(valorFrete), 10) / 100,
      }));
      wizard.setDeliveryData((d) => ({
        ...d,
        shipping: {
          service: tipoFrete,
          price: parseInt(decashnator(valorFrete), 10) / 100,
        },
      }));
    }
    wizard.next();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const { userData, deliveryData, productData } = wizard;
    const body = {
      localId: generateRandomID(),
      userId: userData?._id,
      gradingType: productData?.gradingType,
      autographEvaluation: !!productData?.autographEvaluation,
      ...(normalizeGradingType(productData?.gradingType) === "pre" &&
      productData?.preLacreFormat
        ? { preLacreFormat: productData.preLacreFormat }
        : {}),
      numberOfCards: productData?.numberOfCards,
      delivery: deliveryData?.delivery,
      priority: productData?.priority,
      cardsValue: productData?.declaredValue,
      insurance: productData?.insurance,
      store: deliveryData?.store,
      shipping: deliveryData?.shipping?.price,
      shippingService: deliveryData?.shipping?.service,
      street: deliveryData?.address?.street,
      houseNumber: deliveryData?.address?.houseNumber,
      district: deliveryData?.address?.district,
      city: deliveryData?.address?.city,
      country: deliveryData?.address?.country,
      complement: deliveryData?.address?.complement,
      postalCode: deliveryData?.address?.postalCode,
      state: deliveryData?.address?.state,
      finalPrice: productData?.finalPrice,
      deadline: productData?.deadline,
      status: productData?.status,
      comments: [
        { date: new Date(), text: `Criado pelo admin por ${user?.email}` },
        ...(productData?.comments ?? []),
      ],
      createdByAdmin: true,
    };
    const data = await addProduct(user?.token, body, false);
    if (data?.type !== "success") {
      if (data?.message === "Deadline exceeds limit") {
        badToast("Erro ao criar pedido: Prazo excede o limite permitido");
      } else {
        badToast(`Erro na criação de pedido${data?.message ? `: ${data.message}` : ""}`);
      }
      setSubmitting(false);
      return;
    }
    goodToast("Pedido criado com sucesso!");
    setSubmitting(false);
    navigate(`/manafix/products/${data.data}`);
  };

  const handleBack = () => {
    if (wizard.canPrev) wizard.prev();
    else navigate("/manafix/products");
  };

  const addComment = () => {
    const text = wizard.pendingComment?.trim();
    if (!text) return;
    wizard.setProductData((p) => ({
      ...p,
      comments: [...(p.comments ?? []), { date: new Date(), text }],
    }));
    wizard.setPendingComment("");
  };

  const removeComment = (index) => {
    wizard.setProductData((p) => ({
      ...p,
      comments: (p.comments ?? []).filter((_, i) => i !== index),
    }));
  };

  const storeName = stores.find((s) => s._id === wizard.deliveryData?.store)?.name;

  const renderStep = () => {
    switch (wizard.step) {
      case "user":
        return (
          <MobileSection title="Buscar usuário">
            <MobileFormField label="Nome">
              <input
                className={inputClass}
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Nome"
              />
            </MobileFormField>
            <MobileFormField label="Email">
              <input
                className={inputClass}
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="Email"
              />
            </MobileFormField>
            <MobileFormField label="CPF">
              <input
                className={inputClass}
                value={searchCpf}
                onChange={(e) => setSearchCpf(e.target.value)}
                placeholder="CPF"
              />
            </MobileFormField>
            <MobileFormField label="Telefone">
              <input
                className={inputClass}
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                placeholder="Telefone"
              />
            </MobileFormField>
            <div className="mb-4 flex gap-3">
              <button
                type="button"
                className={primaryBtn}
                onClick={handleSearchUsers}
                disabled={searchLoading}
              >
                Pesquisar
              </button>
              <button
                type="button"
                className={secondaryBtn}
                onClick={() => setCreateUserOpen(true)}
              >
                Criar novo
              </button>
            </div>
            {searchLoading ? (
              <MobileSkeleton lines={3} />
            ) : searchResults.length ? (
              <div className="space-y-2">
                {searchResults.map((u) => (
                  <SelectableCard
                    key={u._id}
                    selected={wizard.userData?._id === u._id}
                    onClick={() => wizard.setUserData(u)}
                  >
                    <p className="font-medium">{u.name}</p>
                    <p className="text-sm text-laranjinha/60">{u.email}</p>
                    <p className="text-sm text-laranjinha/60">Tel: {phoneMask(u.phone)}</p>
                  </SelectableCard>
                ))}
              </div>
            ) : (
              <MobileEmpty message="Nenhum usuário encontrado" />
            )}
          </MobileSection>
        );

      case "delivery":
        return (
          <MobileSection title="Tipo de entrega">
            <div className="mb-4 flex gap-6">
              <label className="inline-flex min-h-[44px] items-center gap-2">
                <input
                  type="radio"
                  name="delivery"
                  value="correios"
                  checked={wizard.deliveryData?.delivery === "correios"}
                  onChange={() =>
                    wizard.setDeliveryData({ delivery: "correios" })
                  }
                />
                Correios
              </label>
              <label className="inline-flex min-h-[44px] items-center gap-2">
                <input
                  type="radio"
                  name="delivery"
                  value="presencial"
                  checked={wizard.deliveryData?.delivery === "presencial"}
                  onChange={() =>
                    wizard.setDeliveryData({ delivery: "presencial" })
                  }
                />
                Presencial
              </label>
            </div>
            {wizard.deliveryData?.delivery === "correios" ? (
              addressesLoading ? (
                <MobileSkeleton lines={2} />
              ) : addresses.length ? (
                <div className="space-y-2">
                  {addresses.map((a) => (
                    <SelectableCard
                      key={a._id}
                      selected={wizard.deliveryData?.address?._id === a._id}
                      onClick={() =>
                        wizard.setDeliveryData((d) => ({
                          ...d,
                          delivery: "correios",
                          address: a,
                        }))
                      }
                    >
                      <p className="font-medium">{a.name}</p>
                      <p className="text-sm text-laranjinha/70">
                        {a.street}, {a.houseNumber} — {a.city}/{a.state}
                      </p>
                      <p className="text-sm text-laranjinha/60">
                        {a.postalCode} — {a.district}
                      </p>
                    </SelectableCard>
                  ))}
                </div>
              ) : (
                <MobileEmpty message="Nenhum endereço cadastrado" />
              )
            ) : null}
            {wizard.deliveryData?.delivery === "correios" ? (
              <button
                type="button"
                className={`${secondaryBtn} mt-4 w-full`}
                onClick={() => setCreateAddressOpen(true)}
              >
                Cadastrar endereço
              </button>
            ) : null}
            {wizard.deliveryData?.delivery === "presencial" ? (
              <MobileFormField label="Loja">
                <select
                  className={selectClass}
                  value={wizard.deliveryData?.store || ""}
                  onChange={(e) =>
                    wizard.setDeliveryData({
                      delivery: "presencial",
                      store: e.target.value,
                    })
                  }
                >
                  <option value="">Selecione</option>
                  {stores.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </MobileFormField>
            ) : null}
          </MobileSection>
        );

      case "product": {
        const gradingType = wizard.productData?.gradingType || "premium";
        return (
          <MobileSection title="Dados do pedido">
            <MobileFormField label="Tipo">
              <select
                className={selectClass}
                value={gradingType}
                onChange={(e) => {
                  const next = e.target.value;
                  wizard.setProductData((p) => ({
                    ...p,
                    gradingType: next,
                    autographEvaluation:
                      gradingTypeSupportsAutograph(next) && p.autographEvaluation,
                  }));
                }}
              >
                {Object.keys(gradingTypes).map((g) => (
                  <option key={g} value={g}>
                    {gradingTypes[g]}
                  </option>
                ))}
              </select>
            </MobileFormField>
            <MobileFormField label="Número de cartas">
              <input
                className={inputClass}
                value={wizard.productData?.numberOfCards || ""}
                onChange={(e) => handleNumberOfCards(e.target.value)}
              />
            </MobileFormField>
            {gradingTypeSupportsAutograph(gradingType) ? (
              <MobileFormField label="Avaliação de autógrafo">
                <label className="inline-flex min-h-[44px] items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!wizard.productData?.autographEvaluation}
                    onChange={(e) =>
                      wizard.setProductData((p) => ({
                        ...p,
                        autographEvaluation: e.target.checked,
                      }))
                    }
                  />
                  Sim
                </label>
              </MobileFormField>
            ) : null}
            {normalizeGradingType(gradingType) === "pre" ? (
              <MobileFormField label="Pré lacre">
                <select
                  className={selectClass}
                  value={wizard.productData?.preLacreFormat || "with_subgrades"}
                  onChange={(e) =>
                    wizard.setProductData((p) => ({
                      ...p,
                      preLacreFormat: e.target.value,
                    }))
                  }
                >
                  {Object.keys(preLacreFormatLabels).map((k) => (
                    <option key={k} value={k}>
                      {preLacreFormatLabels[k]}
                    </option>
                  ))}
                </select>
              </MobileFormField>
            ) : null}
            <MobileFormField label="Prioridade">
              <select
                className={selectClass}
                value={wizard.productData?.priority || "low"}
                onChange={(e) =>
                  wizard.setProductData((p) => ({ ...p, priority: e.target.value }))
                }
              >
                {Object.keys(priorityTypes).map((p) => (
                  <option key={p} value={p}>
                    {priorityTypes[p]}
                  </option>
                ))}
              </select>
            </MobileFormField>
            <MobileFormField label="Valor declarado">
              <input
                className={inputClass}
                value={cashnator(wizard.productData?.declaredValue || 0)}
                onChange={(e) => handleDeclaredValue(e.target.value)}
              />
            </MobileFormField>
          </MobileSection>
        );
      }

      case "shipping":
        return (
          <MobileSection title="Opções de frete">
            {shippingLoading ? (
              <MobileSkeleton lines={3} />
            ) : shippingError ? (
              <MobileEmpty message="Erro ao obter frete. Verifique o endereço." />
            ) : (
              <div className="space-y-2">
                {shippingOptions.map((s, i) => (
                  <SelectableCard
                    key={`${s.service}-${i}`}
                    selected={wizard.selectedShipping?.service === s.service}
                    onClick={() => wizard.setSelectedShipping(s)}
                  >
                    <p className="font-medium">{s.service}</p>
                    <p className="text-lg font-semibold">{cashnatorFloat(s.price)}</p>
                  </SelectableCard>
                ))}
              </div>
            )}
          </MobileSection>
        );

      case "checkout":
        return (
          <MobileSection title="Valores calculados">
            {checkoutLoading ? (
              <MobileSkeleton lines={4} />
            ) : (
              <>
                <MobileFormField label="Taxa de manuseio">
                  <input
                    className={inputClass}
                    value={insurance}
                    disabled={!enableCheckoutEdit}
                    onChange={(e) => {
                      const v = decashnator(e.target.value);
                      if (isNumeric(v)) setInsurance(cashnator(v));
                    }}
                  />
                </MobileFormField>
                <MobileFormField label="Valor final">
                  <input
                    className={inputClass}
                    value={finalPrice}
                    disabled={!enableCheckoutEdit}
                    onChange={(e) => {
                      const v = decashnator(e.target.value);
                      if (isNumeric(v)) setFinalPrice(cashnator(v));
                    }}
                  />
                </MobileFormField>
                <MobileFormField label="Prazo (dias úteis)">
                  <input
                    className={inputClass}
                    value={deadline}
                    disabled={!enableCheckoutEdit}
                    onChange={(e) => {
                      if (!e.target.value || isNumeric(e.target.value)) {
                        setDeadline(e.target.value);
                      }
                    }}
                  />
                </MobileFormField>
                {wizard.deliveryData?.delivery === "correios" ? (
                  <>
                    <MobileFormField label="Tipo frete">
                      <select
                        className={selectClass}
                        disabled={!enableCheckoutEdit}
                        value={tipoFrete}
                        onChange={(e) => setTipoFrete(e.target.value)}
                      >
                        <option value="MINI">MINI</option>
                        <option value="PAC">PAC</option>
                        <option value="SEDEX">SEDEX</option>
                      </select>
                    </MobileFormField>
                    <MobileFormField label="Valor frete">
                      <input
                        className={inputClass}
                        value={valorFrete}
                        disabled={!enableCheckoutEdit}
                        onChange={(e) => {
                          const v = decashnator(e.target.value);
                          if (isNumeric(v)) setValorFrete(cashnator(v));
                        }}
                      />
                    </MobileFormField>
                  </>
                ) : null}
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className={secondaryBtn}
                    onClick={handleRecalculate}
                  >
                    Recalcular
                  </button>
                  <button
                    type="button"
                    className={secondaryBtn}
                    onClick={() => setEnableCheckoutEdit((v) => !v)}
                  >
                    {enableCheckoutEdit ? "Bloquear alterações" : "Liberar alterações"}
                  </button>
                </div>
              </>
            )}
          </MobileSection>
        );

      case "status":
        return (
          <MobileSection title="Status inicial">
            <p className="mb-3 text-sm text-laranjinha/70">
              Selecione o status do pedido ao ser criado
            </p>
            <MobileFormField label="Status">
              <select
                className={selectClass}
                value={wizard.productData?.status || ""}
                onChange={(e) =>
                  wizard.setProductData((p) => ({ ...p, status: e.target.value }))
                }
              >
                <option value="">Selecione</option>
                {Object.keys(statusObject).map((s) => (
                  <option key={s} value={s}>
                    {statusObject[s]}
                  </option>
                ))}
              </select>
            </MobileFormField>
          </MobileSection>
        );

      case "comments":
        return (
          <MobileSection title="Comentários">
            {(wizard.productData?.comments ?? []).length ? (
              <div className="mb-4 space-y-2">
                {(wizard.productData.comments ?? []).map((c, i) => (
                  <div
                    key={`${c.text}-${i}`}
                    className="relative rounded-lg border border-primary/30 p-3"
                  >
                    <button
                      type="button"
                      className="absolute right-2 top-2 text-red-500"
                      onClick={() => removeComment(i)}
                      aria-label="Remover comentário"
                    >
                      ×
                    </button>
                    <p className="text-xs text-laranjinha/60">
                      {formatarDataCompleta(c.date)}
                    </p>
                    <p className="text-sm">{c.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <MobileEmpty message="Nenhum comentário adicionado" />
            )}
            <MobileFormField label="Novo comentário">
              <input
                className={inputClass}
                value={wizard.pendingComment}
                maxLength={300}
                onChange={(e) => wizard.setPendingComment(e.target.value)}
                placeholder="Até 300 caracteres"
              />
            </MobileFormField>
            <button
              type="button"
              className={`${secondaryBtn} mb-4`}
              disabled={!wizard.pendingComment?.trim()}
              onClick={addComment}
            >
              Comentar
            </button>
          </MobileSection>
        );

      case "summary":
        return (
          <>
            <MobileSection title="Usuário">
              <DetailRow label="Nome" value={wizard.userData?.name} />
              <DetailRow label="Email" value={wizard.userData?.email} />
              <DetailRow
                label="Telefone"
                value={phoneMask(wizard.userData?.phone)}
              />
              <DetailRow
                label={
                  wizard.userData?.documentType === "other"
                    ? "Documento"
                    : wizard.userData?.documentType
                }
                value={
                  wizard.userData?.documentType === "other"
                    ? wizard.userData?.documentNumber
                    : cpfMask(wizard.userData?.documentNumber)
                }
              />
            </MobileSection>
            <MobileSection title="Pedido">
              <DetailRow
                label="Tipo"
                value={getGradingTypeLabel(wizard.productData?.gradingType)}
              />
              <DetailRow
                label="Autógrafo"
                value={wizard.productData?.autographEvaluation ? "Sim" : "Não"}
              />
              {normalizeGradingType(wizard.productData?.gradingType) === "pre" &&
              wizard.productData?.preLacreFormat ? (
                <DetailRow
                  label="Pré lacre"
                  value={getPreLacreFormatLabel(wizard.productData.preLacreFormat)}
                />
              ) : null}
              <DetailRow
                label="Quantidade"
                value={wizard.productData?.numberOfCards}
              />
              <DetailRow
                label="Prioridade"
                value={priorityTypes[wizard.productData?.priority]}
              />
              <DetailRow label="Entrega" value={wizard.deliveryData?.delivery} />
              {wizard.deliveryData?.delivery === "correios" ? (
                <>
                  <DetailRow
                    label="Tipo frete"
                    value={wizard.deliveryData?.shipping?.service}
                  />
                  <DetailRow
                    label="Valor frete"
                    value={cashnatorFloat(wizard.deliveryData?.shipping?.price)}
                  />
                </>
              ) : null}
              <DetailRow
                label="Valor dos itens"
                value={cashnator(wizard.productData?.declaredValue)}
              />
              <DetailRow
                label="Taxa de manuseio"
                value={cashnator(wizard.productData?.insurance)}
              />
              <DetailRow
                label="Prazo"
                value={
                  wizard.productData?.deadline
                    ? `${wizard.productData.deadline} dias úteis`
                    : null
                }
              />
              <DetailRow
                label="Preço final"
                value={cashnator(wizard.productData?.finalPrice)}
              />
            </MobileSection>
            <MobileSection title="Entrega">
              {wizard.deliveryData?.delivery === "presencial" ? (
                <DetailRow label="Loja" value={storeName} />
              ) : (
                <>
                  <p className="mb-2 text-sm text-laranjinha/70">
                    Por correios para o endereço:
                  </p>
                  <p className="text-sm font-medium">
                    {wizard.deliveryData?.address?.name}
                  </p>
                  <p className="text-sm text-laranjinha/70">
                    {wizard.deliveryData?.address?.street},{" "}
                    {wizard.deliveryData?.address?.houseNumber} —{" "}
                    {wizard.deliveryData?.address?.city}/
                    {wizard.deliveryData?.address?.state}
                  </p>
                </>
              )}
            </MobileSection>
            <MobileSection title="Comentários">
              {(wizard.productData?.comments ?? []).length ? (
                (wizard.productData.comments ?? []).map((c, i) => (
                  <p key={i} className="mb-1 text-sm">
                    {c.text}
                  </p>
                ))
              ) : (
                <MobileEmpty message="Nenhum comentário" />
              )}
            </MobileSection>
          </>
        );

      default:
        return null;
    }
  };

  const progressLabel = `${wizard.stepIndex + 1} / ${wizard.stepOrder.length}`;

  return (
    <MobilePage
      title={`Criar pedido — ${STEP_TITLES[wizard.step]}`}
      onBack={handleBack}
      action={<span className="text-xs text-laranjinha/60">{progressLabel}</span>}
    >
      {renderStep()}
      <MobileStickyActions>
        {wizard.canPrev ? (
          <button type="button" className={secondaryBtn} onClick={wizard.prev}>
            Voltar
          </button>
        ) : null}
        {wizard.step === "summary" ? (
          <button
            type="button"
            className={primaryBtn}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? "Criando…" : "Criar"}
          </button>
        ) : (
          <button
            type="button"
            className={primaryBtn}
            disabled={!wizard.canNext}
            onClick={handleNext}
          >
            Próximo
          </button>
        )}
      </MobileStickyActions>
      {createUserOpen ? (
        <RegisterModal
          handleClose={() => setCreateUserOpen(false)}
          productCreation
          handleUser={handleUserCreated}
        />
      ) : null}
      <MobileNewAddressSheet
        open={createAddressOpen}
        onClose={() => setCreateAddressOpen(false)}
        user={user}
        userId={wizard.userData?._id}
        onCreated={handleAddressCreated}
      />
    </MobilePage>
  );
}
